import React, { useState } from 'react';
import { Button, Modal } from 'equasion-lib';

type CleanupManualEntryProps = {
  /** At the zone step the zone name is typed; after it, a product code. */
  mode: 'zone' | 'product';
  zoneName: string;
  busy: boolean;
  onSubmit: (text: string) => void;
  onClose: () => void;
};

// equasion's ButtonProps type omits onClick (it reaches the <button> through ...rest at runtime);
// a spread is not excess-property checked.
const click = (onClick: () => void) => ({ onClick });

/**
 * Manual entry for a label or pack that will not scan. A typed zone name goes through the same
 * `zoneByScanCode` lookup a scan does, and a typed code through the same line match — a way around a
 * torn label, never around the check that the picker is at the stop's zone.
 */
function CleanupManualEntry({
  mode,
  zoneName,
  busy,
  onSubmit,
  onClose
}: CleanupManualEntryProps) {
  const [text, setText] = useState('');
  const ready = !!text.trim();
  const submit = () => {
    if (!busy && ready) onSubmit(text.trim());
  };

  return (
    <Modal
      className="cleanup-manual-modal"
      modalOpen
      setModalOpen={(nextOpen?: boolean) => {
        if (!nextOpen) onClose();
      }}
    >
      <form
        className="cleanup-manual"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <strong>
          {mode === 'zone' ? 'Type the zone name' : 'Type the product code'}
        </strong>
        {mode === 'zone' ? (
          <p>
            For a label that will not scan. You should be at <strong>{zoneName}</strong>.
          </p>
        ) : (
          <p>
            The NDC, UPC or GTIN printed on the pack. Its line in{' '}
            <strong>{zoneName}</strong> is picked in full.
          </p>
        )}
        <label className="cleanup-manual__field">
          <span>{mode === 'zone' ? 'Zone name' : 'NDC / UPC / GTIN'}</span>
          <input
            name={mode === 'zone' ? 'zoneName' : 'code'}
            autoFocus
            autoComplete="off"
            {...(mode === 'zone'
              ? { autoCapitalize: 'characters' }
              : { inputMode: 'numeric' as const })}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        <Button type="primary" fullWidth isSubmit disabled={busy || !ready}>
          {busy ? 'Checking…' : mode === 'zone' ? 'Check zone' : 'Pick it'}
        </Button>
        <Button type="secondary" fullWidth {...click(onClose)}>
          Cancel
        </Button>
      </form>
    </Modal>
  );
}

export default CleanupManualEntry;
